import React, {useEffect, useState} from "react";
import * as XLSX from "xlsx";
import {useTheme} from "@mui/material/styles";
import {
  AppBar,
  Card,
  Divider,
  Grid,
  Paper,
  Snackbar,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  IconButton,
  TextField,
  Box,
  Tooltip,
  Typography
} from "@mui/material";
import {DataGrid} from "@mui/x-data-grid";
import CloseIcon from "@mui/icons-material/Close";
import {
  LastPage,
  FirstPage,
  NavigateBefore,
  NavigateNext
} from "@mui/icons-material";
import MDBox from "components/MDBox";
import MDButton from "components/MDButton";
import MDInput from "components/MDInput";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import {GET} from "APIclient";
import properties from "constants/ApplicationConstants";

function CustomPagination({ 
  page, 
  pageSize, 
  totalRows, 
  totalPages, 
  onPageChange, 
  onGoToLastPage,
  loading 
}) {
  const handleFirstPage = () => {
    onPageChange(0);
  };

  const handlePreviousPage = () => {
    onPageChange(Math.max(0, page - 1));
  };

  const handleNextPage = () => {
    onPageChange(Math.min(totalPages - 1, page + 1));
  };

  const handleLastPage = () => {
    onGoToLastPage();
  };

  const startRow = page * pageSize + 1;
  const endRow = Math.min((page + 1) * pageSize, totalRows);

  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        p: 1,
        borderTop: '1px solid #e0e0e0'
      }}
    >
      <Typography variant="caption" sx={{ color: '#666' }}>
        {totalRows > 0 ? `${startRow}-${endRow} of ${totalRows}` : 'No rows'}
      </Typography>
      
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <Typography variant="caption" sx={{ mr: 2, color: '#666' }}>
          Page {page + 1} of {totalPages || 1}
        </Typography>
        
        <Tooltip title="First Page">
          <IconButton
            onClick={handleFirstPage}
            disabled={page === 0 || loading}
            size="small"
          >
            <FirstPage />
          </IconButton>
        </Tooltip>
        
        <Tooltip title="Previous Page">
          <IconButton
            onClick={handlePreviousPage}
            disabled={page === 0 || loading}
            size="small"
          >
            <NavigateBefore />
          </IconButton>
        </Tooltip>
        
        <Tooltip title="Next Page">
          <IconButton
            onClick={handleNextPage}
            disabled={page >= totalPages - 1 || loading}
            size="small"
          >
            <NavigateNext />
          </IconButton>
        </Tooltip>
        
        <Tooltip title="Last Page">
          <IconButton
            onClick={handleLastPage}
            disabled={page >= totalPages - 1 || loading}
            size="small"
            sx={{
              backgroundColor: properties?.colors?.bgcolorbluethem || '#1976d2',
              color: 'white',
              '&:hover': {
                backgroundColor: properties?.colors?.bgcolorbluethem || '#1565c0',
              },
              '&.Mui-disabled': {
                backgroundColor: '#e0e0e0',
                color: '#9e9e9e'
              }
            }}
          >
            <LastPage />
          </IconButton>
        </Tooltip>
      </Box>
    </Box>
  );
}

const BalanceLeaveReport = () => {
  const theme = useTheme();
  const [balanceLeaveReports, setBalanceLeaveReports] = useState([]);
  const [filteredBalanceLeaveReports, setFilteredBalanceLeaveReports] = useState([]);
  const [paginationModel, setPaginationModel] = useState({page: 0, pageSize: 20});
  const [totalRows, setTotalRows] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const [openModal, setOpenModal] = useState(false);
  const [selectedReport, setSelectedReport] = useState(null);
  const [searchEmployeeId, setSearchEmployeeId] = useState("");
  const [searchEmployeeName, setSearchEmployeeName] = useState("");
  const [snackOpen, setSnackOpen] = useState(false);
  const [snackMessage, setSnackMessage] = useState("");
  const [snackColor, setSnackColor] = useState("success");
  const [loading, setLoading] = useState(false);
  const currentYear = new Date().getFullYear();

  const columns = [
    {field: "id", headerName: "#", width: 30, align: "left", headerAlign: "left"},
    {field: "empId", headerName: "EMP ID", width: 70, align: "left", headerAlign: "left"},
    {
      field: "employeeName",
      headerName: "Employee Name",
      width: 190,
      align: "left",
      headerAlign: "left"
    },
    {
      field: "plannedLeaves",
      headerName: "Planned Leaves",
      width: 130,
      align: "left",
      headerAlign: "left"
    },
    {
      field: "unplannedLeaves",
      headerName: "Unplanned Leaves",
      width: 160,
      align: "left",
      headerAlign: "left"
    },
    {
      field: "coffLeaves",
      headerName: "COFF Leaves",
      width: 160,
      align: "left",
      headerAlign: "left"
    },
    {
      field: "balancedUplLeavesLastYear",
      headerName: `Balanced UPL (${currentYear - 1})`,
      width: 160,
      align: "left",
      headerAlign: "left"
    }
  ];

  const fetchBalanceLeaveReports = async (page, pageSize, empId, empName) => {
    try {
      setLoading(true);
      const url = `admin/getEmployeeBalanceLeavesReports?page=${page}&size=${pageSize}&empId=${empId}&empName=${empName}`;
      console.log("Fetching data with URL:", url);
      const response = await GET(url);
      console.log("API Response:", response);

      if (response.status === 200 && Array.isArray(response.data?.data?.paginatedData)) {
        const data = response.data.data.paginatedData;
        const totalRecords = response.data.data.totalItems || (data.length > 0 ? 1000 : 0);
        const totalPages = response.data.data.totalPages || Math.ceil(totalRecords / pageSize);
        console.log("Paginated Data:", data);
        console.log("Total Records:", totalRecords);
        console.log("Total Pages:", totalPages);

        const formattedData = data.map((item, index) => {
          const row = {
            id: page * pageSize + index + 1,
            empId: item.empId,
            employeeName: item.employeeName,
            plannedLeaves: item.plannedLeaves,
            unplannedLeaves: item.unplannedLeaves,
            coffLeaves: item.coffLeaves,
            balancedUplLeavesLastYear: item.balancedUplLeavesLastYear
          };
          console.log("Formatted Row:", row);
          return row;
        });

        console.log("Formatted Data:", formattedData);
        setBalanceLeaveReports(formattedData);
        setFilteredBalanceLeaveReports(formattedData);
        setTotalRows(totalRecords);
        setTotalPages(totalPages);

        if (data.length === 0 && (empId || empName)) {
          setSnackMessage("No matching reports found.");
          setSnackColor("warning");
          setSnackOpen(true);
        }
      } else {
        throw new Error("Invalid response format");
      }
    } catch (error) {
      console.error("Error fetching balance leave reports:", error);
      setSnackMessage("Failed to fetch balance leave reports.");
      setSnackColor("error");
      setSnackOpen(true);
      setBalanceLeaveReports([]);
      setFilteredBalanceLeaveReports([]);
      setTotalRows(0);
      setTotalPages(0);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    console.log("Fetching data for page:", paginationModel.page);
    fetchBalanceLeaveReports(paginationModel.page, paginationModel.pageSize, searchEmployeeId, searchEmployeeName);
  }, [paginationModel.page, paginationModel.pageSize]);

  const handleCloseModal = () => {
    setOpenModal(false);
    setSelectedReport(null);
  };

  const handlePaginationChange = newPaginationModel => {
    console.log("Pagination changed to:", newPaginationModel);
    setPaginationModel({
      page: newPaginationModel.page,
      pageSize: newPaginationModel.pageSize
    });
    fetchBalanceLeaveReports(
      newPaginationModel.page, 
      newPaginationModel.pageSize, 
      searchEmployeeId, 
      searchEmployeeName
    );
    setTimeout(() => {
      window.scrollTo(0, 0);
    }, 0);
  };

  const handlePageChange = (newPage) => {
    const newPaginationModel = { ...paginationModel, page: newPage };
    setPaginationModel(newPaginationModel);
    fetchBalanceLeaveReports(newPage, paginationModel.pageSize, searchEmployeeId, searchEmployeeName);
    setTimeout(() => {
      window.scrollTo(0, 0);
    }, 0);
  };

  const handleGoToLastPage = () => {
    if (totalPages > 0) {
      const lastPageIndex = totalPages - 1;
      const newPaginationModel = { ...paginationModel, page: lastPageIndex };
      setPaginationModel(newPaginationModel);
      fetchBalanceLeaveReports(lastPageIndex, paginationModel.pageSize, searchEmployeeId, searchEmployeeName);
      setTimeout(() => {
        window.scrollTo(0, 0);
      }, 0);
    }
  };

  const handleSearch = () => {
    const newPaginationModel = { ...paginationModel, page: 0 };
    setPaginationModel(newPaginationModel);
    fetchBalanceLeaveReports(0, paginationModel.pageSize, searchEmployeeId, searchEmployeeName);
  };

  const handleKeyPress = event => {
    if (event.key === "Enter") {
      handleSearch();
    }
  };

  const handleDownloadExcel = () => {
    const worksheet = XLSX.utils.json_to_sheet(filteredBalanceLeaveReports);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Balance Leave Report");
    XLSX.writeFile(workbook, "BalanceLeave_report.xlsx");
  };

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <MDBox>
        <AppBar position="static">
          <Card>
            <MDBox pt={2} px={2} pb={2}>
              <MDBox sx={{ display: "grid", gridTemplateColumns: "repeat(12, 1fr)", gap: 2, alignItems: "center" }}>
                <Typography
                  sx={{
                    gridColumn: "span 2",
                    color: "textPrimary",
                    fontFamily: properties.fontFamily,
                    fontSize: "15px",
                    fontWeight: "bold"
                  }}
                >
                  Balance Leave Report
                </Typography>
                <MDInput
                  sx={{gridColumn: "span 2"}}
                  value={searchEmployeeId}
                  onChange={e => setSearchEmployeeId(e.target.value)}
                  onKeyPress={handleKeyPress}
                  label="Employee ID"
                />
                <MDInput
                  sx={{gridColumn: "span 2"}}
                  value={searchEmployeeName}
                  onChange={e => setSearchEmployeeName(e.target.value)}
                  onKeyPress={handleKeyPress}
                  label="Employee Name"
                />
                <MDButton
                  sx={{gridColumn: "span 2", background: properties.colors.bgcolorbluethem}}
                  variant="gradient"
                  color="info"
                  onClick={handleSearch}
                >
                  Search
                </MDButton>
                <MDButton
                  sx={{gridColumn: "span 2"}}
                  variant="gradient"
                  color="success"
                  onClick={handleDownloadExcel}
                >
                  Download
                </MDButton>
              </MDBox>
            </MDBox>
          </Card>
        </AppBar>
            <MDBox>
              <Paper>
                <DataGrid
                  rows={filteredBalanceLeaveReports}
                  columns={columns}
                  paginationMode="server"
                  rowCount={totalRows}
                  pageSizeOptions={[10, 20, 50]}
                  paginationModel={paginationModel}
                  onPaginationModelChange={handlePaginationChange}
                  hideFooter
                  loading={loading}
                  getRowId={row => row.empId}
                  sx={{
                    height: "450px",
                    fontWeight:400,
                    fontFamily: properties.fontFamily,
                    fontSize: "14px",
                    "& .MuiDataGrid-cell": {
                      textAlign: "left",
                    },
                    "& .MuiDataGrid-scrollbar": {
                      width: "10px !important",  
                      // backgroundColor: "transparent",
                    },
                  }}
                />
                <CustomPagination
                  page={paginationModel.page}
                  pageSize={paginationModel.pageSize}
                  totalRows={totalRows}
                  totalPages={totalPages}
                  onPageChange={handlePageChange}
                  onGoToLastPage={handleGoToLastPage}
                  loading={loading}
                />
              </Paper>
            </MDBox>
        <Snackbar
          open={snackOpen}
          autoHideDuration={3000}
          onClose={() => setSnackOpen(false)}
          anchorOrigin={{vertical: "top", horizontal: "right"}}
        >
          <Alert onClose={() => setSnackOpen(false)} severity={snackColor}>
            {snackMessage}
          </Alert>
        </Snackbar>
        <Dialog open={openModal} onClose={handleCloseModal} fullWidth maxWidth="sm">
          <DialogTitle>
            Balanced Leaves Report
            <IconButton edge="end" onClick={handleCloseModal}>
              <CloseIcon />
            </IconButton>
          </DialogTitle>
          <DialogContent>
            {selectedReport && (
              <form>
                <TextField
                  label="Employee ID"
                  value={selectedReport.empId || "N/A"}
                  fullWidth
                  InputProps={{readOnly: true}}
                  margin="normal"
                />
                <TextField
                  label="Employee Name"
                  value={selectedReport.employeeName || "N/A"}
                  fullWidth
                  InputProps={{readOnly: true}}
                  margin="normal"
                />
                <TextField
                  label="Planned Leaves"
                  value={selectedReport.plannedLeaves || "N/A"}
                  fullWidth
                  InputProps={{readOnly: true}}
                  margin="normal"
                />
                <TextField
                  label="Unplanned Leaves"
                  value={selectedReport.unplannedLeaves || "N/A"}
                  fullWidth
                  InputProps={{readOnly: true}}
                  margin="normal"
                />
                <TextField
                  label="COFF Leaves"
                  value={selectedReport.coffLeaves || "N/A"}
                  fullWidth
                  InputProps={{readOnly: true}}
                  margin="normal"
                />
                <TextField
                  label="Balanced UPL"
                  value={selectedReport.balancedUplLeavesLastYear || "N/A"}
                  fullWidth
                  InputProps={{readOnly: true}}
                  margin="normal"
                />
              </form>
            )}
          </DialogContent>
        </Dialog>
      </MDBox>
    </DashboardLayout>
  );
};

export default BalanceLeaveReport;